// screens/EngagementSpread.tsx — K2's consumer, the surface
// (c3-lifecycle-kernel.md): one bounded engagement — a design partnership, a
// build, a change — whose STRIP is the kind's own lifecycle_variants entry,
// not a stage every engagement shares (that generic twelve-stage strip was
// withdrawn; K2 replaced it with a lifecycle PER KIND).
//
// The record itself rides the generic engine (getRecord); the strip's own
// ordered states come from the type's schema (`lifecycleVariants.variants
// [record.fields.kind].states` — K2's own effectiveLifecycle, mirrored
// here since the web holds no Core import); the dated crossing list and the
// Move-to action are K2's own door (transitionEngagementLifecycle /
// listEngagementTransitions).
import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  getRecord,
  getRecordTypeSchema,
  transitionEngagementLifecycle,
  listEngagementTransitions,
  listOutcomes,
  type RecordRow,
  type RecordTypeSchema,
  type EngagementTransitionApi,
} from '../lib/api';
import { BackLink } from '../components/BackLink';
import { OutcomeSheet } from '../components/OutcomeSheet';
import { PlanPane } from '../components/PlanPane';
import './spread.css';

export interface EngagementSpreadProps {
  tenantId: string;
  id: string;
  onBack: () => void;
  onOpenAccount: (accountId: string) => void;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; record: RecordRow; schema: RecordTypeSchema; transitions: EngagementTransitionApi[] };

function when(iso: string | null): string {
  if (!iso) return '';
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

const REALISED_CHIP: Record<string, string> = { not_yet: 'not yet', partly: 'partly', yes: 'yes', no: 'no' };

function outcomeRow(o: RecordRow, onOpen: (id: string) => void) {
  const measure = typeof o.fields.measure === 'string' ? o.fields.measure : o.display_name;
  const realised = REALISED_CHIP[String(o.fields.realised ?? 'not_yet')] ?? 'not yet';
  return (
    <button type="button" class="sp__row" key={o.id} onClick={() => onOpen(o.id)}>
      <span class="sp__row-top">
        <span class="sp__row-name">{measure}</span>
        <span class="sp__row-when">{o.fields.review_on ? `review ${when(o.fields.review_on as string)}` : ''}</span>
      </span>
      <span class="sp__row-sub">
        {String(o.fields.baseline_value ?? '—')} → {String(o.fields.target_value ?? '—')} · now {String(o.fields.current_value ?? '—')} · <span class="sp__chip">{realised}</span>
      </span>
    </button>
  );
}

export function EngagementSpread({ tenantId, id, onBack, onOpenAccount }: EngagementSpreadProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [moving, setMoving] = useState(false);
  const [moveTo, setMoveTo] = useState<string>('');
  const [moveDate, setMoveDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNoteText] = useState('');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  // K4 — the Outcomes pane, same OutcomeSheet the Account spread uses.
  const [outcomes, setOutcomes] = useState<RecordRow[]>([]);
  const [outcomesError, setOutcomesError] = useState<string | null>(null);
  const [outcomeSheet, setOutcomeSheet] = useState<'new' | string | null>(null);

  function refreshOutcomes() {
    return listOutcomes(tenantId, { engagementId: id })
      .then(setOutcomes)
      .catch((err: unknown) => setOutcomesError(err instanceof ApiError ? err.message : 'outcomes could not be read'));
  }

  function refresh() {
    return Promise.all([getRecord(tenantId, id), getRecordTypeSchema(tenantId, 'engagement'), listEngagementTransitions(tenantId, id)])
      .then(([record, schema, transitions]) => setLoad({ phase: 'ready', record, schema, transitions }))
      .catch((err: unknown) => {
        setLoad({
          phase: 'error',
          message: err instanceof ApiError ? err.message : 'this engagement could not be read',
        });
      });
  }

  useEffect(() => {
    setLoad({ phase: 'loading' });
    void refresh();
    void refreshOutcomes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, id]);

  if (load.phase === 'loading') {
    return (
      <div class="sp">
        <BackLink label="Clients" onClick={onBack} />
        <div class="sp__say">One moment…</div>
      </div>
    );
  }
  if (load.phase === 'error') {
    return (
      <div class="sp">
        <BackLink label="Clients" onClick={onBack} />
        <div class="sp__say">
          This engagement could not be read.
          <br />
          <span class="sp__say-soft">{load.message}</span>
        </div>
      </div>
    );
  }

  const { record, schema, transitions } = load;
  const kind = typeof record.fields.kind === 'string' ? record.fields.kind : '';
  const title = typeof record.fields.title === 'string' ? record.fields.title : record.display_name;
  const accountId = typeof record.fields.account_id === 'string' ? record.fields.account_id : null;
  const states = schema.lifecycleVariants?.variants[kind]?.states ?? [];
  const currentIndex = states.indexOf(record.lifecycle_state ?? '');
  // effective_on per state — the LATEST transition landing on it, since a
  // state may be re-entered (e.g. managed_service's active <-> at_risk).
  const effectiveByState = new Map<string, string>();
  for (const t of [...transitions].sort((a, b) => a.effectiveOn.localeCompare(b.effectiveOn))) {
    effectiveByState.set(t.toState, t.effectiveOn);
  }
  const orderedTransitions = [...transitions].sort(
    (a, b) => b.effectiveOn.localeCompare(a.effectiveOn) || b.changedAt.localeCompare(a.changedAt)
  );

  async function move() {
    if (!moveTo) return;
    setRefusal(null);
    setBusy(true);
    try {
      await transitionEngagementLifecycle(tenantId, id, { toState: moveTo, effectiveOn: moveDate, note: note.trim() || undefined });
      setMoving(false);
      setNoteText('');
      await refresh();
    } catch (err) {
      setRefusal(err instanceof ApiError ? err.message : 'that move was refused');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="sp">
      <BackLink label="Clients" onClick={onBack} />

      <header class="sp__hero-top">
        <div>
          <div class="sp__label">
            <span class="sp__chip">{kind.replace(/_/g, ' ')}</span>
            {accountId ? (
              <button type="button" class="sp__crumb-cur" style={{ cursor: 'pointer' }} onClick={() => onOpenAccount(accountId)}>
                the account
              </button>
            ) : null}
          </div>
          <h1 class="sp__title">{title}</h1>
          <p class="sp__sub">
            {when((record.fields.started_on as string) ?? null)}
            {record.fields.ended_on ? ` – ${when(record.fields.ended_on as string)}` : ''}
          </p>
        </div>
      </header>

      <div class="sp__steps">
        {states.map((state, i) => (
          <div class="sp__step-cell" key={state}>
            <div
              class={`sp__step${i <= currentIndex ? ' sp__step--reached' : ''}${i === currentIndex ? ' sp__step--current' : ''}`}
            >
              <span class="sp__dot" />
              <span class="sp__step-tx">
                {state.replace(/_/g, ' ')}
                {effectiveByState.has(state) ? <><br /><small>{when(effectiveByState.get(state)!)}</small></> : null}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div class="sp__grid">
        <div class="sp__body">
          <div class="sp__sec"><span>Transitions</span></div>
          {orderedTransitions.length === 0 ? (
            <div class="sp__empty">No transitions recorded yet.</div>
          ) : (
            orderedTransitions.map((t) => (
              <div class="sp__row" key={t.id}>
                <span class="sp__row-top">
                  <span class="sp__row-name">{t.fromState ?? '(start)'} → {t.toState}</span>
                  <span class="sp__row-when">{when(t.effectiveOn)}</span>
                </span>
                {t.note ? <span class="sp__row-sub">{t.note}</span> : null}
              </div>
            ))
          )}

          <div class="sp__sec" style={{ marginTop: '16px' }}><span>Outcomes</span></div>
          {outcomesError ? (
            <div class="sp__age sp__age--late">{outcomesError}</div>
          ) : outcomes.length === 0 ? (
            <div class="sp__empty">No outcomes yet — every engagement begins with a measurable baseline and target.</div>
          ) : (
            outcomes.map((o) => outcomeRow(o, (oid) => setOutcomeSheet(oid)))
          )}
          <div class="sp__acts">
            <button type="button" class="sp__act" onClick={() => setOutcomeSheet('new')}>New outcome</button>
          </div>

          <PlanPane tenantId={tenantId} engagementId={id} />
        </div>
        <div class="sp__aside">
          <div class="sp__sec"><span>Move</span></div>
          {!moving ? (
            <div class="sp__acts">
              <button type="button" class="sp__act" onClick={() => { setMoveTo(states[0] ?? ''); setMoving(true); }}>
                Move to…
              </button>
            </div>
          ) : (
            <div class="sp__acts" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
              <select
                class="sp__act"
                value={moveTo}
                disabled={busy}
                onChange={(e) => setMoveTo((e.target as HTMLSelectElement).value)}
              >
                {states.map((s) => <option value={s} key={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
              <input
                class="sp__act"
                type="date"
                value={moveDate}
                disabled={busy}
                onInput={(e) => setMoveDate((e.target as HTMLInputElement).value)}
              />
              <input
                class="sp__act"
                type="text"
                placeholder="Note (optional)"
                value={note}
                disabled={busy}
                onInput={(e) => setNoteText((e.target as HTMLInputElement).value)}
              />
              <button type="button" class="sp__act" disabled={busy} onClick={() => void move()}>Move</button>
              <button type="button" class="sp__act" disabled={busy} onClick={() => setMoving(false)}>Never mind</button>
            </div>
          )}
          {refusal ? <p class="sp__age sp__age--late">{refusal}</p> : null}
        </div>
      </div>

      {outcomeSheet && accountId ? (
        <OutcomeSheet
          tenantId={tenantId}
          accountId={accountId}
          engagementId={id}
          outcomeId={outcomeSheet === 'new' ? null : outcomeSheet}
          onClose={() => setOutcomeSheet(null)}
          onSaved={() => void refreshOutcomes()}
        />
      ) : null}
    </div>
  );
}
